"use client";

import { useEffect, useRef, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { initPostHog } from "@/lib/posthog";

function PostHogTracker({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Initialize PostHog
    initPostHog();
  }, []);

  useEffect(() => {
    // Debounce page view tracking to prevent Safari's history.replaceState() rate limit
    // Safari enforces max 100 calls per 10 seconds
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Track page views on route change (only in production)
      if (
        process.env.NODE_ENV === "production" &&
        pathname &&
        posthog &&
        posthog.__loaded
      ) {
        let url = window.origin + pathname;
        if (searchParams && searchParams.toString()) {
          url = url + "?" + searchParams.toString();
        }
        posthog.capture("$pageview", {
          $current_url: url,
        });
      }
    }, 300); // 300ms debounce to batch rapid navigation events

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [pathname, searchParams]);

  return <>{children}</>;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<>{children}</>}>
      <PostHogTracker>{children}</PostHogTracker>
    </Suspense>
  );
}
